/**
 * googie — the design system behind the Googie v7 redesign.
 *
 * Ported from the design canvas (`OrbitGoogieV7.dc.html` +
 * `OrbitGoogiePages.dc.html`, "CRM design overhaul request"). One
 * module owns everything the redesign shares so no screen invents its
 * own sky, palette or nav accent:
 *
 *   SKIES      — the four time-of-day gradients, their star density,
 *                glow colour and greeting.
 *   resolveTod — the studio's local hour → which sky.
 *   NAV_ACCENT — one dot colour per nav destination.
 *
 * The look: a deep-space gradient that changes through the day,
 * twinkling stars, an atomic starburst, Bricolage Grotesque over DM
 * Sans, and Googie colour — orange, teal, mustard, red — on circular
 * badges and pill chrome.
 *
 * WHAT THIS REDESIGN DOES NOT TOUCH (Rennan, explicitly): Nova's 3D
 * character and every other Nova visual stay exactly as they ship —
 * the canvas draws its own flat Nova, and we do not use it. Neither
 * do we take the canvas's hyperspace/warp animation; the app keeps
 * the one it has.
 */

export type Tod = 'dawn' | 'midday' | 'dusk' | 'night';

export interface Sky {
  /** Three gradient stops, top to bottom (a fourth, #0B0B0C, is fixed). */
  sky: [string, string, string];
  /** 0–1 opacity of the star layer. */
  stars: number;
  /** The soft radial that sits behind the top-right of every screen. */
  glow: string;
  label: string;
  greet: string;
}

export const SKIES: Record<Tod, Sky> = {
  dawn: { sky: ['#1E1836', '#5E3550', '#C98B78'], stars: 0.35, glow: '#F5A277', label: 'Dawn', greet: 'Good morning' },
  midday: { sky: ['#2F5A78', '#2A4A6A', '#5F5E5A'], stars: 0, glow: '#CFE3EE', label: 'Midday', greet: 'Good afternoon' },
  dusk: { sky: ['#2C1B45', '#7A3960', '#D07E62'], stars: 0.6, glow: '#E88C6E', label: 'Golden hour', greet: 'Good evening' },
  night: { sky: ['#0A0F1F', '#111832', '#150F28'], stars: 1, glow: '#8FB4FF', label: 'Night', greet: 'Good evening' },
};

/** Which sky the studio's clock is under. Same buckets as the canvas. */
export function resolveTod(now: Date = new Date()): Tod {
  const h = now.getHours();
  if (h >= 5 && h < 8) return 'dawn';
  if (h >= 8 && h < 16) return 'midday';
  if (h >= 16 && h < 21) return 'dusk';
  return 'night';
}

/**
 * GOOGIE-9 — the same day, on paper.
 *
 * The canvas's second theme isn't "the dark one, inverted": it's a
 * paper ground with ink type. But the time of day is the whole
 * conceit of this design — the app should feel different at 6am than
 * at 9pm — so light mode keeps the clock and changes the material.
 * Dawn is cream going peach, midday is a cool white, golden hour is
 * apricot, and night is a pale slate: still paper, still light enough
 * to read ink on, but unmistakably a different hour.
 *
 * Stars and the comet are off in every light sky. A star field on
 * paper is a smudge.
 */
const SKIES_LIGHT: Record<Tod, { sky: [string, string, string]; glow: string }> = {
  dawn: { sky: ['#FDF3E7', '#F8E3D6', '#F0C9B4'], glow: '#F5A277' },
  midday: { sky: ['#F7F9FB', '#EFF3F7', '#E3E9F0'], glow: '#CFE3EE' },
  dusk: { sky: ['#FDF1E4', '#F7DFCB', '#EEC2A6'], glow: '#E88C6E' },
  night: { sky: ['#F2F1F6', '#E9E8F0', '#DCDAE6'], glow: '#8FB4FF' },
};

/** The CSS custom properties one sky publishes to everything under it. */
export function skyVars(tod: Tod, light = false): Record<string, string> {
  if (light) {
    const l = SKIES_LIGHT[tod];
    return {
      '--g-sky': `linear-gradient(180deg,${l.sky[0]} 0%,${l.sky[1]} 46%,${l.sky[2]} 100%)`,
      '--g-stars': '0',
      '--g-glow': l.glow,
      '--g-comet': '0',
    };
  }
  const t = SKIES[tod];
  return {
    '--g-sky': `linear-gradient(180deg,${t.sky[0]} 0%,${t.sky[1]} 38%,${t.sky[2]} 62%,#0B0B0C 100%)`,
    '--g-stars': String(t.stars),
    '--g-glow': t.glow,
    '--g-comet': t.stars >= 0.6 ? '1' : '0',
  };
}

/**
 * Nav dot colours. Keyed by NAV_DESTINATIONS id (navDestinations.ts)
 * so the rail can never drift from the routes it opens; the canvas's
 * own labels (Passport, Clients, …) map onto the same ids.
 */
export const NAV_ACCENT: Record<string, string> = {
  today: '#3B7DD8',
  chat: '#7FD1D3',
  clients: '#E63B2E',
  work: '#9B8CFF',
  business: '#CFE3EE',
  money: '#B5D33D',
  passport: '#E1B24C',
  calendar: '#9B8CFF',
  inquiries: '#E63B2E',
};

export const googieFontsHref =
  'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap';

/** Googie colour, straight off the canvas. */
/**
 * Orbit's blue. It isn't a Googie accent — it's the app's own —
 * which is exactly why the quick-action marks wear it: every icon on
 * the Today rail is the same blue, so the rail reads as one control
 * with six stops rather than six unrelated buttons.
 */
export const ORBIT_BLUE = '#3B7DD8';

/**
 * Ink or ivory — whichever can actually be read on `hex`.
 *
 * Rec. 709 luma, which is close enough for picking between two
 * colours and doesn't need a colour library. The 0.62 threshold is
 * tuned to this palette: lime, cyan and sky land above it and take
 * ink, teal and orange land below and take ivory.
 */
export function onColor(hex: string): string {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(n.slice(0, 2), 16) / 255;
  const g = parseInt(n.slice(2, 4), 16) / 255;
  const b = parseInt(n.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.62 ? '#1A1814' : '#F4EFE6';
}

export const G = {
  ink: '#F4EFE6',
  paper: '#1A1814',
  orange: '#E63B2E',
  teal: '#3E9C9E',
  mustard: '#E1B24C',
  red: '#D9463B',
  lime: '#B5D33D',
  sky: '#7FB6EA',
  violet: '#B98CE6',
  green: '#7FB069',
  cyan: '#7FD1D3',
} as const;
